import { useState, useEffect } from "react";

const ASSET_API = "https://bn360.bluenett.solutions/asset-api";

export default function AssetsPanel({ theme }) {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({ total: 0, online: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const bg = theme?.bg || "#0f172a";
  const card = theme?.bgCard || "#1e293b";
  const border = theme?.border || "rgba(148,163,184,0.15)";
  const txt = theme?.text || "#e2e8f0";
  const muted = theme?.textMuted || "#94a3b8";
  const accent = "#10b981";

  const load = async () => {
    try {
      const r = await fetch(`${ASSET_API}/list`);
      const d = await r.json();
      setAssets(d.assets || []);
      setSummary({ total: d.total || 0, online: d.online || 0 });
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const usageColor = (pct) => pct >= 85 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#10b981";

  const Bar = ({ pct }) => (
    <div style={{ background: "rgba(148,163,184,0.15)", borderRadius: 6, height: 8, overflow: "hidden", width: "100%" }}>
      <div style={{ width: `${Math.min(pct || 0, 100)}%`, height: "100%", background: usageColor(pct), borderRadius: 6, transition: "width .3s" }} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", background: bg, padding: "24px", fontFamily: "'Inter',sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: `linear-gradient(135deg,${accent},#059669)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>💻</div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: txt }}>Bluenett Assets</div>
          <div style={{ fontSize: 12, color: muted }}>Employee systems monitoring</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: txt }}>{summary.total}</div>
            <div style={{ fontSize: 10, color: muted, textTransform: "uppercase" }}>Systems</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: accent }}>{summary.online}</div>
            <div style={{ fontSize: 10, color: muted, textTransform: "uppercase" }}>Online</div>
          </div>
        </div>
      </div>

      {loading && <div style={{ color: muted, textAlign: "center", padding: 40 }}>Loading assets…</div>}

      {!loading && assets.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💻</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: txt, marginBottom: 6 }}>No systems reporting yet</div>
          <div style={{ fontSize: 13 }}>Install the BN360 agent on employee systems to see them here.</div>
        </div>
      )}

      {/* Asset grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
        {assets.map((a, i) => (
          <div key={i} onClick={() => setSelected(a)} style={{
            background: card, border: `1px solid ${border}`, borderRadius: 14, padding: 18, cursor: "pointer",
            transition: "all .15s", position: "relative",
          }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = accent}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = border}
          >
            {/* Employee + status */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${accent}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, color: accent }}>
                {(a.employee || "?").charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: txt }}>{a.employee || "Unknown"}</div>
                <div style={{ fontSize: 11, color: muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.hostname}</div>
              </div>
              <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: a.online ? accent : muted }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: a.online ? accent : muted, boxShadow: a.online ? `0 0 6px ${accent}` : "none" }} />
                {a.online ? "Online" : "Offline"}
              </span>
            </div>

            {/* OS + specs */}
            <div style={{ fontSize: 11, color: muted, marginBottom: 10 }}>
              {a.os} · {a.cpu_cores} cores · {a.ram_total_gb}GB RAM
            </div>

            {/* security + apps badges */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 6, fontWeight: 600,
                background: a.protected ? "#10b98122" : "#ef444422", color: a.protected ? "#10b981" : "#ef4444" }}>
                {a.protected ? "🛡️ Protected" : "⚠️ No AV"}
              </span>
              <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 6, fontWeight: 600, background: "rgba(148,163,184,0.15)", color: muted }}>
                📦 {a.apps_count || 0} apps
              </span>
              {a.docker_count > 0 && (
                <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 6, fontWeight: 600, background: "#3b82f622", color: "#3b82f6" }}>
                  🐳 {a.docker_count} containers
                </span>
              )}
            </div>

            {/* Usage bars */}
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                  <span style={{ color: muted }}>CPU</span><span style={{ color: usageColor(a.cpu_percent), fontWeight: 600 }}>{a.cpu_percent}%</span>
                </div>
                <Bar pct={a.cpu_percent} />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                  <span style={{ color: muted }}>RAM</span><span style={{ color: usageColor(a.ram_percent), fontWeight: 600 }}>{a.ram_used_gb}/{a.ram_total_gb}GB ({a.ram_percent}%)</span>
                </div>
                <Bar pct={a.ram_percent} />
              </div>
              {a.disks && Object.entries(a.disks).slice(0, 1).map(([k, v]) => (
                <div key={k}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: muted }}>Disk ({k})</span><span style={{ color: usageColor(v.percent), fontWeight: 600 }}>{v.used_gb}/{v.total_gb}GB ({v.percent}%)</span>
                  </div>
                  <Bar pct={v.percent} />
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12, fontSize: 10, color: muted }}>Last seen: {a.last_seen || "-"}</div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: card, border: `1px solid ${border}`, borderRadius: 16, padding: 24, maxWidth: 480, width: "100%", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <div style={{ width: 46, height: 46, borderRadius: 11, background: `${accent}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: accent }}>{(selected.employee || "?").charAt(0).toUpperCase()}</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: txt }}>{selected.employee}</div>
                <div style={{ fontSize: 12, color: muted }}>{selected.hostname} · {selected.user}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ marginLeft: "auto", background: "transparent", border: "none", color: muted, fontSize: 22, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 16 }}>{selected.os} · {selected.cpu_cores} cores · {selected.ram_total_gb}GB · uptime {selected.uptime_hours}h</div>
            {selected.top_processes && selected.top_processes.length > 0 && (
              <div style={{ fontSize: 12, color: muted, marginBottom: 12, padding: "8px 12px", background: bg, borderRadius: 8 }}>
                🔥 Highest RAM usage: <span style={{ color: "#f59e0b", fontWeight: 700 }}>{selected.top_processes[0].name}</span> ({selected.top_processes[0].mem_mb} MB)
              </div>
            )}
            <div style={{ fontSize: 13, fontWeight: 700, color: txt, marginBottom: 10 }}>Top Processes (by memory)</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(selected.top_processes || []).map((p, j) => (
                <div key={j} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "7px 12px", background: bg, borderRadius: 8 }}>
                  <span style={{ color: txt }}>{p.name}</span><span style={{ color: muted }}>{p.mem_mb} MB</span>
                </div>
              ))}
            </div>

            {/* Security tools */}
            <div style={{ fontSize: 13, fontWeight: 700, color: txt, margin: "18px 0 10px" }}>Security {selected.protected ? "🛡️" : "⚠️"}</div>
            {selected.security_tools && selected.security_tools.length > 0 ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {selected.security_tools.map((t, k) => (
                  <span key={k} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, background: "#10b98122", color: "#10b981", fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#ef4444" }}>No antivirus / security tool detected</div>
            )}

            {/* Docker containers */}
            <div style={{ fontSize: 13, fontWeight: 700, color: txt, margin: "18px 0 10px" }}>Docker Containers ({selected.docker_count || 0}) 🐳</div>
            {selected.docker_containers && selected.docker_containers.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {selected.docker_containers.map((c, k) => (
                  <div key={k} style={{ fontSize: 12, padding: "8px 12px", background: bg, borderRadius: 8 }}>
                    <div style={{ color: txt, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ color: muted, fontSize: 11, marginTop: 2 }}>{c.image} · {c.status}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: muted }}>No running containers</div>
            )}

            {/* Installed apps */}
            <div style={{ fontSize: 13, fontWeight: 700, color: txt, margin: "18px 0 10px" }}>Installed Applications ({selected.apps_count || 0})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(selected.installed_apps || []).map((app, k) => (
                <span key={k} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: bg, color: muted, border: `1px solid ${border}` }}>{app}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
